/**
 * Session-only mutable state for Automation: pausing/resuming a workflow and
 * cancelling a scheduled action. Everything else is regenerated from the
 * tenant fixture and clock on every read (`engine.ts`), so a mutation here is
 * layered on top rather than replacing the generator.
 */

import type { ActivityEntry, AutomationStatus, ScheduledAction, Workflow } from '@/api/automation'

import { buildActivity, buildScheduledActions, buildWorkflows } from './engine'
import type { TenantFixture } from '../tenants/types'

export class UnknownWorkflowError extends Error {
  constructor(id: string) {
    super(`Unknown workflow: ${id}`)
    this.name = 'UnknownWorkflowError'
  }
}

export class AutomationStore {
  private readonly statusOverrides = new Map<string, AutomationStatus>()
  private readonly cancelledScheduledIds = new Set<string>()

  constructor(private readonly tenant: TenantFixture) {}

  workflows(today: string): Workflow[] {
    return buildWorkflows(this.tenant, today).map((workflow) => {
      const status = this.statusOverrides.get(workflow.id)
      return status ? { ...workflow, status } : workflow
    })
  }

  workflow(id: string, today: string): Workflow | null {
    return this.workflows(today).find((workflow) => workflow.id === id) ?? null
  }

  setStatus(id: string, status: AutomationStatus, today: string): Workflow {
    if (!this.tenant.automations.some((fixture) => fixture.id === id)) throw new UnknownWorkflowError(id)
    this.statusOverrides.set(id, status)
    return this.workflow(id, today) as Workflow
  }

  scheduledActions(today: string): ScheduledAction[] {
    const workflows = this.workflows(today)
    return buildScheduledActions(this.tenant, workflows, today).map((action) =>
      this.cancelledScheduledIds.has(action.id) && action.state === 'pending'
        ? { ...action, state: 'cancelled' as const }
        : action,
    )
  }

  cancelScheduledAction(id: string, today: string): ScheduledAction[] {
    this.cancelledScheduledIds.add(id)
    return this.scheduledActions(today)
  }

  activity(today: string): ActivityEntry[] {
    return buildActivity(this.tenant, this.workflows(today), today)
  }
}
